import {
    Box,
    Button,
    Chip,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Eye,
    Pencil,
    Search,
    Trash2,
    Truck,
    X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDocument } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import MobileCrudList from '../../component/common/MobileCrud';
import { KitField, KitSelect } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';
import StatusChip from '../../component/common/StatusChip';
import { customerInitials } from '../../component/customer/customerMobileUtils';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import SupplierViewDialog from './View';

const MOBILE_PAGE_SIZE = 20;

const AVATAR_TONES = ['soft', 'purple', 'green', 'amber'];
// Tom do avatar pelo nome (estável entre renders): alterna soft/roxo/verde/âmbar como no protótipo.
function supplierTone(name) {
    let hash = 0;
    String(name || '').split('').forEach((ch) => { hash = (hash * 31 + ch.charCodeAt(0)) % 997; });
    return AVATAR_TONES[hash % AVATAR_TONES.length];
}

const TruckIcon = protoIcon('truck');
const EyeIcon = protoIcon('eye');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');

// Filtro aplicado só ao clicar "Buscar"/Enter — mesma técnica de
// `customers/List.js` (report-toolbar §6.1): o estado do campo (`typeFilter`/
// `search`) é distinto do filtro efetivamente aplicado (`appliedType`/
// `appliedSearch`), que alimenta os chips removíveis e a busca no servidor.
// Tipo (PJ/PF) é inferido no servidor pelo documento (11 dígitos = PF).
export default function List() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação.
    const [mobilePage, setMobilePage] = useState(0);

    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    // Paginação server-side: `page` é 0-based (DefaultTable); a API usa 1-based.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPJ, setTotalPJ] = useState(0);
    const [totalPF, setTotalPF] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);

    // Valores dos campos do formulário de filtro (não aplicados ainda).
    const [typeFilter, setTypeFilter] = useState('');
    const [search, setSearch] = useState('');
    // Filtros efetivamente aplicados — alimentam os chips removíveis.
    const [appliedType, setAppliedType] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    const [viewSupplierId, setViewSupplierId] = useState(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [supplierToDelete, setSupplierToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Busca a página atual no servidor sempre que filtros aplicados/página/tamanho mudam.
    useEffect(() => {
        let cancelled = false;
        const requestPage = isMobileShell ? mobilePage : page;
        const params = { page: requestPage + 1, page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize };
        if (appliedType) params.type = appliedType;
        if (appliedSearch.trim()) params.search = appliedSearch.trim();

        setLoading(true);
        httpClient.get('/suppliers', { params })
            .then((response) => {
                if (cancelled) return;
                const rows = response.data?.items || [];
                const total = response.data?.total || 0;
                // Página ficou além do fim (ex.: excluiu o último item da última página).
                if (!isMobileShell && rows.length === 0 && total > 0 && page > 0) {
                    setPage(Math.ceil(total / pageSize) - 1);
                    return;
                }
                if (isMobileShell && requestPage > 0) {
                    setSuppliers((prev) => [...prev, ...rows.filter((row) => !prev.some((p) => p.id === row.id))]);
                } else {
                    setSuppliers(rows);
                }
                setTotalCount(total);
                setTotalPJ(response.data?.total_pj || 0);
                setTotalPF(response.data?.total_pf || 0);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error fetching suppliers:', error);
                setSuppliers([]);
                setTotalCount(0);
                setTotalPJ(0);
                setTotalPF(0);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, pageSize, appliedType, appliedSearch, reloadKey, isMobileShell, mobilePage]);

    // Mobile: a busca é aplicada sozinha (debounce), sem botão "Buscar".
    useEffect(() => {
        if (!isMobileShell || search.trim() === appliedSearch) return undefined;
        const timer = setTimeout(() => {
            setAppliedSearch(search.trim());
            setMobilePage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [search, appliedSearch, isMobileShell]);

    const handleApplyFilters = () => {
        setAppliedType(typeFilter);
        setAppliedSearch(search);
        setPage(0);
        setMobilePage(0);
    };

    const handleClearFilters = () => {
        setTypeFilter('');
        setSearch('');
        setAppliedType('');
        setAppliedSearch('');
        setPage(0);
        setMobilePage(0);
    };

    const handleRemoveTypeFilter = () => {
        setTypeFilter('');
        setAppliedType('');
        setPage(0);
    };

    const handleRemoveSearchFilter = () => {
        setSearch('');
        setAppliedSearch('');
        setPage(0);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    const handleViewSupplier = (row) => {
        setViewSupplierId(row.id);
        setOpenViewDialog(true);
    };

    const handleDeleteSupplier = (row) => {
        setSupplierToDelete(row);
        setOpenDialogConfirmDelete(true);
    };

    const confirmDelete = async () => {
        try {
            setDeleting(true);
            await httpClient.delete(`/suppliers/${supplierToDelete.id}`);
            setOpenDialogConfirmDelete(false);
            setSupplierToDelete(null);
            setReloadKey((k) => k + 1);
            setSnackbarMessage('Fornecedor excluído com sucesso!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Error deleting supplier:', error);
            setSnackbarMessage('Erro ao excluir fornecedor.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        {
            path: 'legal_name',
            name: 'Razão Social',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                        {row.legal_name}
                    </Typography>
                    
                </Box>
            ),
        },
        {
            path: 'trade_name',
            name: 'Nome Fantasia',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: row.trade_name ? 'text.secondary' : 'text.disabled' }}>
                    {row.trade_name || '—'}
                </Typography>
            ),
        },
        {
            path: 'document',
            name: 'Documento',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                        {formatDocument(row.document) || '—'}
                    </Typography>
                    
                </Box>
            ),
        },
        {
            path: 'cell_phone',
            name: 'Contato',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography sx={{ fontSize: '13px', color: row.cell_phone ? 'text.secondary' : 'text.disabled' }}>
                        {row.cell_phone || row.phone_number || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                        {row.email || ''}
                    </Typography>
                </Box>
            ),
        },
        {
            path: 'city',
            name: 'Cidade',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: row.city ? 'text.secondary' : 'text.disabled' }}>
                    {row.city || '—'}
                </Typography>
            ),
        },
    ];

    const actions = [
        ...(hasPermission('supplier.view') ? [{
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => handleViewSupplier(row),
                tooltip: 'Visualizar',
                style: { cursor: 'pointer' }
            }
        }] : []),
        ...(hasPermission('supplier.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => navigate(`/suppliers/edit/${row.id}`),
                tooltip: 'Editar',
                style: { cursor: 'pointer' }
            }
        }] : []),
        ...(hasPermission('supplier.delete') ? [{
            icon: <Trash2 size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => handleDeleteSupplier(row),
                tooltip: 'Excluir',
                style: { cursor: 'pointer' }
            }
        }] : []),
    ];

    const sharedDialogs = (
        <>
            <SupplierViewDialog
                open={openViewDialog}
                supplierId={viewSupplierId}
                onClose={() => setOpenViewDialog(false)}
            />

            <DialogForm
                iconType="warning"
                open={openDialogConfirmDelete}
                title="Atenção"
                confirmButtonText="Sim, Excluir"
                cancelButtonText="Cancelar"
                content={`Tem certeza que deseja excluir o fornecedor "${supplierToDelete?.legal_name}"?`}
                secondaryContent="Esta ação não pode ser desfeita. O fornecedor será removido permanentemente do sistema."
                onSubmit={confirmDelete}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setSupplierToDelete(null);
                }}
                loading={deleting}
                loadingText="Removendo..."
            />
        </>
    );

    // Shell mobile: lista do protótipo `fornecedores/lista.html` (mesmos dados, filtros e ações).
    if (isMobileShell) {
        const docKind = (row) => ((row.document || '').replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ');
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget="Fornecedores"
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileCrudList
                    items={suppliers}
                    getKey={(row) => row.id}
                    getRow={(row) => ({
                        avatar: { text: customerInitials(row.legal_name), tone: supplierTone(row.legal_name) },
                        title: row.legal_name,
                        sub: row.document ? `${docKind(row)} ${formatDocument(row.document)}` : (row.trade_name || ''),
                        chevron: true,
                    })}
                    loading={loading}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Nome ou CNPJ…"
                    filter={{
                        active: Boolean(appliedType),
                        onOpen: () => setTypeFilter(appliedType),
                        onApply: handleApplyFilters,
                        onClear: handleClearFilters,
                        children: (
                            <KitField label="Tipo">
                                <KitSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Tipo">
                                    <option value="">Todos</option>
                                    <option value="pj">Pessoa Jurídica</option>
                                    <option value="pf">Pessoa Física</option>
                                </KitSelect>
                            </KitField>
                        ),
                    }}
                    emptyIcon={TruckIcon}
                    emptyTitle="Nenhum fornecedor encontrado"
                    emptyText="Ajuste a busca ou os filtros para ver outros fornecedores."
                    hasMore={suppliers.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    fabLabel={hasPermission('supplier.create') ? 'Novo' : undefined}
                    onNew={() => navigate('/suppliers/new')}
                    rowTitle={(row) => row.legal_name}
                    rowActions={(row) => [
                        ...(hasPermission('supplier.view') ? [{ label: 'Visualizar', icon: EyeIcon, onClick: () => handleViewSupplier(row) }] : []),
                        ...(hasPermission('supplier.update') ? [{ label: 'Editar', icon: EditIcon, onClick: () => navigate(`/suppliers/edit/${row.id}`) }] : []),
                        ...(hasPermission('supplier.delete') ? [{ label: 'Excluir', icon: TrashIcon, destructive: true, onClick: () => handleDeleteSupplier(row) }] : []),
                    ]}
                />
                {sharedDialogs}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget="Fornecedores"
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ação (Novo fornecedor). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <Truck size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Fornecedores
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Cadastre e consulte os fornecedores usados nas compras e reposição de estoque.
                            </Typography>
                        </Box>
                    </Box>

                    {hasPermission('supplier.create') && (
                        <NewItemButton label="Novo fornecedor" onClick={() => navigate('/suppliers/new')} />
                    )}
                </Box>

                {/* report-toolbar — Tipo + Buscar, em card separado. */}
                <FilterToolbar
                    plain
                    activeCount={typeFilter ? 1 : 0}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 300px' }}>
                            <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                Buscar
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Razão social, nome fantasia ou documento…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box>
                            <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                Tipo
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <Select
                                    value={typeFilter}
                                    displayEmpty
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    renderValue={(value) => (value === 'pj' ? 'Pessoa Jurídica' : value === 'pf' ? 'Pessoa Física' : 'Todos')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="pj">Pessoa Jurídica</MenuItem>
                                    <MenuItem value="pf">Pessoa Física</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                </FilterToolbar>

                {/* chips-row — filtros ativos removíveis + contagem/quebra PJ/PF. */}
                <FilterChips>
                    {appliedType && (
                        <Chip
                            label={`Tipo: ${appliedType === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}`}
                            size="small"
                            onDelete={handleRemoveTypeFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {appliedSearch && (
                        <Chip
                            label={`Busca: "${appliedSearch}"`}
                            size="small"
                            onDelete={handleRemoveSearchFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} fornecedor${totalCount === 1 ? '' : 'es'} encontrado${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`${totalPJ} Pessoa Jurídica / ${totalPF} Pessoa Física`}
                        size="small"
                        sx={{ bgcolor: 'success.bg', color: 'success.main' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={suppliers}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={hasPermission('supplier.view') ? handleViewSupplier : undefined}
                    rowPerPage={8}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="fornecedores"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>

            {sharedDialogs}
        </PageScaffold>
    );
}
